import { useMemo, useState } from 'react';
import type { CellValueChangedEvent, ColDef, ICellRendererParams } from 'ag-grid-community';
import {
    NJButton,
    NJFormItem,
    NJIconButton,
    NJInlineMessage,
    NJModal,
    NJModalContent,
    NJSegmentedControl,
    NJSegmentedControlButton,
    NJSelectItem,
    NJSelectRoot,
    NJTooltip,
} from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import { AddButtonDivider, DeleteRowButton } from './EditableRows';
import type { FuelContract, VariablePrice } from './types';
import { inputValue } from './ui';

type Granularity = 'monthly' | 'hourly';
type Row = { id: number; date: string; price: number | null };

const API_DATE_REGEX = /^(\d{4}-\d{2}|\d{4}-\d{2}-\d{2} \d{2})$/;
const UI_MONTHLY = /^\d{2}\/\d{2}\/\d{4}$/;
const UI_HOURLY = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/;
const SUBTITLES: Record<Granularity | '', string> = {
    hourly: 'Format accepté : jj/mm/aaaa hh:mm',
    monthly: 'Format accepté : jj/mm/aaaa',
    '': 'Formats acceptés : jj/mm/aaaa hh:mm (horaire) ou jj/mm/aaaa (mensuel)',
};
const DATE_ERROR = "Erreur : le format de date n'est pas respecté sur au moins une ligne du tableau. Le format attendu est aaaa-mm ou aaaa-mm-jj hh";

let seed = 0;
const emptyRow = (): Row => ({ id: ++seed, date: '', price: null });
const pad = (n: number) => String(n).padStart(2, '0');
const formatPrice = (value: unknown) => (typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const hasPrice = (row: Row) => row.price !== null && String(row.price) !== '';

/** API (yyyy-mm | yyyy-mm-dd hh) → UI (dd/mm/yyyy | dd/mm/yyyy hh:mm). */
export function fromApiDate(date: string): string {
    const monthly = /^(\d{4})-(\d{2})$/.exec(date);
    if (monthly) return `01/${monthly[2]}/${monthly[1]}`;
    const hourly = /^(\d{4})-(\d{2})-(\d{2}) (\d{2})$/.exec(date);
    if (hourly) return `${hourly[3]}/${hourly[2]}/${hourly[1]} ${hourly[4]}:00`;
    return date;
}

/** UI (dd/mm/yyyy | dd/mm/yyyy hh:mm) → API (yyyy-mm | yyyy-mm-dd hh). */
export function toApiDate(date: string): string {
    const monthly = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    if (monthly) return `${monthly[3]}-${monthly[2]}`;
    const hourly = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):\d{2}$/.exec(date);
    if (hourly) return `${hourly[3]}-${hourly[2]}-${hourly[1]} ${hourly[4]}`;
    return date;
}

/** Nemo `generateDates`: monthly = every 1st of month (inclusive), hourly = every hour while < end. */
export function generateDates(start: Date, end: Date, granularity: Granularity): string[] {
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) return [];
    const dates: string[] = [];
    if (granularity === 'monthly') {
        const current = new Date(start.getFullYear(), start.getMonth(), 1);
        const last = new Date(end.getFullYear(), end.getMonth(), 1);
        while (current <= last) {
            dates.push(`01/${pad(current.getMonth() + 1)}/${current.getFullYear()}`);
            current.setMonth(current.getMonth() + 1);
        }
        return dates;
    }
    const current = new Date(start);
    while (current < end) {
        dates.push(`${pad(current.getDate())}/${pad(current.getMonth() + 1)}/${current.getFullYear()} ${pad(current.getHours())}:00`);
        current.setTime(current.getTime() + 3_600_000);
    }
    return dates;
}

function DateCell({ value }: ICellRendererParams<Row, string>) {
    const invalid = !!value && !UI_MONTHLY.test(value) && !UI_HOURLY.test(value);
    return <span className={`production-contracts-ellipsis${invalid ? ' production-contracts-invalid' : ''}`} title={value ?? ''}>{value ?? ''}</span>;
}

/** Nemo `VariablePricesModal` (CenterModal « <contrat> - Modification des prix variables »). */
export default function VariablePricesModal({ contract, onClose, onValidate, isPending }: {
    contract: FuelContract;
    onClose: () => void;
    onValidate: (prices: VariablePrice[]) => void;
    isPending?: boolean;
}) {
    const [rows, setRows] = useState<Row[]>(() => (contract.variablePrices.length
        ? contract.variablePrices.map((p) => ({ id: ++seed, date: fromApiDate(p.date), price: p.price }))
        : [emptyRow()]));
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [granularity, setGranularity] = useState<Granularity | ''>('hourly');
    const [error, setError] = useState<string | null>(null);

    const canGenerate = start !== '' && end !== '' && granularity !== '';
    const canValidate = rows.some((row) => !!row.date && hasPrice(row)) && !rows.some((row) => !!row.date !== hasPrice(row)) && !isPending;

    const columns = useMemo<ColDef<Row>[]>(() => [
        {
            field: 'date',
            headerName: 'Date',
            width: 200,
            editable: true,
            cellRenderer: DateCell,
            valueParser: ({ newValue }) => fromApiDate(String(newValue ?? '').trim()),
        },
        {
            field: 'price',
            headerName: 'Prix (€/u)',
            width: 150,
            editable: true,
            cellEditor: 'agNumberCellEditor',
            valueFormatter: ({ value }) => formatPrice(value),
        },
        {
            headerName: 'Actions',
            width: 120,
            flex: 0,
            editable: false,
            cellRenderer: ({ data }: ICellRendererParams<Row>) => <DeleteRowButton onClick={() => setRows((current) => current.filter((row) => row.id !== data?.id))} />,
        },
    ], []);

    const changeGranularity = (value?: string) => {
        const next = (value ?? '') as Granularity | '';
        if (next !== granularity) { setStart(''); setEnd(''); }
        setGranularity(next);
    };

    const generate = () => {
        if (!canGenerate) return;
        setRows(generateDates(new Date(start), new Date(end), granularity as Granularity).map((date) => ({ id: ++seed, date, price: null })));
    };

    const downloadCsv = () => {
        const csv = ['Date;Prix (€/u)', ...rows.filter((row) => row.date && row.price !== null).map((row) => `${row.date};${row.price}`)].join('\n');
        const url = URL.createObjectURL(new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${contract.name}_variable_prices.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const validate = () => {
        const prices = rows.filter((row) => row.date && hasPrice(row)).map((row) => ({ date: toApiDate(row.date), price: Number(row.price) }));
        if (prices.some((p) => !API_DATE_REGEX.test(p.date))) {
            setError(DATE_ERROR);
            return;
        }
        setError(null);
        onValidate(prices);
    };

    const dateType = granularity === 'monthly' ? 'date' : 'datetime-local';

    return (
        <NJModal isOpen setIsOpen={(open) => !open && onClose()} closeOnEscape closeOnBackdropClick>
            <NJModalContent
                id="variable-prices-modal"
                className="production-contracts-prices-modal"
                style={{ width: 850, maxWidth: "90vw" }}
                title={`${contract.name} - Modification des prix variables`}
                onClose={onClose}
                closeLabel="Fermer"
                footer={
                    <>
                        <NJButton label="Fermer" variant="secondary" onClick={onClose} />
                        <NJButton label="Valider" disabled={!canValidate} onClick={validate} />
                    </>
                }
            >
                <div data-testid="variable-prices-modal">
                    <p className="production-contracts-prices-subtitle">{SUBTITLES[granularity]}</p>
                    {error && <NJInlineMessage variant="error">{error}</NJInlineMessage>}

                    <div className="production-contracts-generator-card">
                        <p className="production-contracts-generator-title">Générateur de dates</p>
                        <div className="production-contracts-generator">
                            <NJFormItem key={`start-${dateType}`} id="vp-start-date" type={dateType} label="Date de début" value={start} onChange={(event) => setStart(inputValue(event))} />
                            <NJFormItem key={`end-${dateType}`} id="vp-end-date" type={dateType} label="Date de fin" value={end} onChange={(event) => setEnd(inputValue(event))} />
                            <NJSelectRoot
                                id="vp-granularity"
                                label="Granularité"
                                value={granularity}
                                onChange={changeGranularity}
                                buttonDefaultValueLabel="—"
                                listNavigationLabel=""
                            >
                                <NJSelectItem value="monthly">Mensuelle</NJSelectItem>
                                <NJSelectItem value="hourly">Horaire</NJSelectItem>
                            </NJSelectRoot>
                        </div>
                        <div className="production-contracts-generator-actions">
                            <NJButton label="Générer les dates" variant="secondary" disabled={!canGenerate} onClick={generate} />
                        </div>
                    </div>

                    <div className="production-contracts-prices-toolbar">
                        <NJSegmentedControl label="table-graph-options" size="sm" value="table" onChange={() => undefined}>
                            <NJSegmentedControlButton value="table" label="Tableau" iconName="table_chart" />
                            <NJSegmentedControlButton value="chart" label="Graphique" iconName="bar_chart" isDisabled />
                        </NJSegmentedControl>
                        <NJTooltip text="Télécharger" hasArrow placement="top">
                            <NJIconButton icon="download" label="Télécharger" scale="xs" onClick={downloadCsv} />
                        </NJTooltip>
                    </div>

                    <div className="production-contracts-prices-table">
                        <DataTable<Row>
                            testId="variable-prices-table"
                            header={columns}
                            data={rows}
                            defaultColDef={{ flex: 1, minWidth: 100, sortable: false, resizable: false, headerClass: 'production-contracts-header-center', cellClass: 'production-contracts-center' }}
                            onCellValueChanged={(event: CellValueChangedEvent<Row>) => setRows((current) => current.map((row) => (row.id === event.data.id ? { ...event.data } : row)))}
                        />
                        <div className="production-contracts-add-first">
                            <AddButtonDivider onClick={() => setRows((current) => [...current, emptyRow()])} testId="add-variable-price-row" />
                        </div>
                    </div>
                </div>
            </NJModalContent>
        </NJModal>
    );
}
